import { useMemo } from 'react';
import { BasicTooltip } from '@nivo/tooltip';
import NivoBarChart from './NivoBarChart';
import { useLegendHoverDim } from '../../utils/useLegendHoverDim';
import { buildWrappedLegends, legendMarginBottom, AXIS_TITLE_HEIGHT } from '../../utils/nivoLegend';
import useElementWidth from '../../hooks/useElementWidth';

type BarItem = { label: string; value: number; color: string };

type PartyBarChartProps = {
    items: BarItem[];
    height?: string;
    axisFormat: (value: number) => string;
    axisTitle?: string;
    legendLabel?: (item: BarItem) => string;
    legendItemWidth?: number;
    maxLabelLength?: number;
    maxValue?: number | 'auto';
    onBarClick?: (datum: { id: string | number }) => void;
};

// Shared by analyses that render one horizontal bar per party as a single group
// (no index categories) - PartiesUnity, SummaryVotesGraph and AttendanceAnalysis
// all used to duplicate this legend/dimming/bar-shaping setup.
function PartyBarChart({
    items,
    height = '250px',
    axisFormat,
    axisTitle,
    legendLabel,
    legendItemWidth = 170,
    maxLabelLength,
    maxValue = 'auto',
    onBarClick
}: PartyBarChartProps) {
    const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();
    const colorMap = useMemo(() => Object.fromEntries(items.map((d) => [d.label, d.color])), [items]);
    const { colors, legends } = useLegendHoverDim(colorMap);

    const keys = items.map((d) => d.label);
    const barData = [Object.fromEntries([['id', ''], ...items.map((d) => [d.label, d.value])])];

    // The x-axis title (when set) renders right after the tick labels, so the legend
    // needs to start further down to avoid overlapping it.
    const legendStartY = 45 + (axisTitle ? AXIS_TITLE_HEIGHT : 0);
    const { legends: legendRows, rowCount: legendRowCount } = buildWrappedLegends(
        items.map((d) => ({ id: d.label, label: legendLabel ? legendLabel(d) : d.label, color: d.color })),
        {
            containerWidth: containerWidth || undefined,
            itemWidth: legendItemWidth,
            maxLabelLength,
            startY: legendStartY,
            onMouseEnter: legends.onMouseEnter,
            onMouseLeave: legends.onMouseLeave
        }
    );

    return (
        <div ref={containerRef} style={{ width: '100%' }}>
            <NivoBarChart
                height={height}
                data={barData}
                keys={keys}
                indexBy='id'
                layout='horizontal'
                colors={colors}
                innerPadding={4}
                valueScale={{ type: 'linear', min: 0, max: maxValue }}
                axisLeft={null}
                axisBottom={{ format: axisFormat }}
                xTitle={axisTitle}
                margin={{ top: 20, right: 20, bottom: legendMarginBottom(legendRowCount, { startY: legendStartY }), left: 15 }}
                onClick={onBarClick}
                legends={legendRows}
                tooltip={({ id, value, color }) => <BasicTooltip id={`${id}: ${axisFormat(Number(value))}`} color={color} enableChip />}
            />
        </div>
    );
}

export default PartyBarChart;
